/**
 * v0.5① 日報機能 P-3 (§14-3-A フィールド規則): 1項目分の入力UI。
 * value_kind ごとに出し分け、carryover/computed/is_not_applicable の表現規則を実装する。
 */
import { History } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { DailyReportField, DailyReportFieldValue, DailyReportCarryover } from '@/lib/daily-report-api';

// design-expertレビュー Major是正 (提出前プレビュー、2026-09-09): disabled時に見た目上も
// 編集不可と分かるようにする (従来はブラウザ既定の淡い網掛けのみで、確認専用画面で
// 「まだ編集できる」と誤解されるリスクがあった)。
export const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring disabled:bg-surface-sunken disabled:text-text-sub';
export const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none text-h3`;

const parseFieldOptions = (json: string | null): { value: string; label: string }[] => {
  if (!json) return [];
  try {
    const raw = JSON.parse(json) as unknown;
    if (Array.isArray(raw)) {
      return raw.map((o) => (typeof o === 'string' ? { value: o, label: o } : (o as { value: string; label: string })));
    }
  } catch {
    // 不正なJSONは選択肢なし扱い (壊れた設定を画面クラッシュにしない)
  }
  return [];
};

export function DailyReportFieldRow({
  field, carryoverHint, disabled, errorMessage, fieldRef, onValueChange,
}: {
  field: DailyReportField;
  carryoverHint?: DailyReportCarryover | null;
  disabled: boolean;
  errorMessage?: string;
  fieldRef?: (el: HTMLDivElement | null) => void;
  onValueChange: (value: DailyReportFieldValue, isNotApplicable: boolean) => void;
}) {
  const isCarryover = field.source === 'carryover';
  const isComputed = field.value_kind === 'computed' || field.source === 'computed';
  // 遡及引き継ぎ (N日前まで遡った場合) は alert.* へ格上げする (§4-1-1「黙って埋めない」)。
  const isOverdueCarryover = isCarryover && (carryoverHint?.days_ago ?? 0) > 1;
  const canToggleNotApplicable = !isComputed && field.value_kind !== 'attachment' && field.value_kind !== 'line_items'
    && (field.visibility === 'optional' || field.visibility === 'conditional' || field.visibility === 'required');
  // ⚠️ design-expert CRITICAL是正 (2026-09-09): money/number/text の<input>にラベルを紐付ける
  // (`htmlFor`/`id`)。select は SelectTrigger 側の aria-label、check はlabelでinputを包む形で
  // 既に紐付いており対象外。
  const inputId = `drf-${field.field_definition_id}`;
  const isLabelableKind = !isComputed && (field.value_kind === 'money' || field.value_kind === 'number' || field.value_kind === 'text');

  const wrapperCls = [
    'py-2',
    isOverdueCarryover ? 'border-l-4 border-alert-border bg-alert-bg/40 pl-2 -ml-2 rounded-r' : '',
    isCarryover && !isOverdueCarryover ? 'border-l-4 border-chrome-base bg-surface-sunken pl-2 -ml-2 rounded-r' : '',
  ].filter(Boolean).join(' ');

  const renderInput = () => {
    if (field.is_not_applicable) {
      return <div className="text-body text-text-muted italic">自店では対象外</div>;
    }
    if (isComputed) {
      return (
        <div className={`${inputCls} text-h3 bg-surface-sunken text-text-body flex items-center`}>
          {field.value === null || field.value === undefined ? '—' : String(field.value)}
        </div>
      );
    }
    switch (field.value_kind) {
      case 'money':
      case 'number':
        return (
          <input
            id={inputId} type="number" inputMode="numeric" step="any"
            className={numberInputCls} disabled={disabled} aria-required={field.visibility === 'required'}
            defaultValue={typeof field.value === 'number' ? field.value : ''}
            onBlur={(e) => {
              const raw = e.target.value;
              onValueChange(raw === '' ? null : Number(raw), false);
            }}
          />
        );
      case 'select': {
        const options = parseFieldOptions(field.field_options_json);
        return (
          <Select value={typeof field.value === 'string' ? field.value : ''} onValueChange={(v) => onValueChange(v, false)}>
            <SelectTrigger aria-label={field.label} aria-required={field.visibility === 'required'} disabled={disabled} className={inputCls}><SelectValue placeholder="選択してください" /></SelectTrigger>
            <SelectContent>
              {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      }
      case 'check':
        return (
          <label className="flex items-center gap-2 text-body text-text-body min-h-[44px]">
            <input type="checkbox" className="w-5 h-5" disabled={disabled}
              checked={field.value === true} onChange={(e) => onValueChange(e.target.checked, false)} />
            {field.label}
          </label>
        );
      case 'text':
      default:
        return (
          <input
            id={inputId} type="text" className={inputCls} disabled={disabled} aria-required={field.visibility === 'required'}
            defaultValue={typeof field.value === 'string' ? field.value : ''}
            onBlur={(e) => onValueChange(e.target.value === '' ? null : e.target.value, false)}
          />
        );
    }
  };

  return (
    <div ref={fieldRef} className={wrapperCls}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {field.value_kind !== 'check' && (
          <label className="text-label text-text-sub" htmlFor={isLabelableKind ? inputId : undefined}>
            {field.label}
            {field.visibility === 'required' && <span className="text-text-sub ml-0.5" aria-hidden>*</span>}
          </label>
        )}
        {canToggleNotApplicable && (
          <label className="flex items-center gap-1.5 text-caption text-text-sub min-h-[44px]">
            <input type="checkbox" className="w-4 h-4" disabled={disabled}
              checked={field.is_not_applicable}
              onChange={(e) => onValueChange(null, e.target.checked)} />
            対象外
          </label>
        )}
      </div>
      <div className="mt-1">{renderInput()}</div>
      {isCarryover && !field.is_not_applicable && (
        <p className={`mt-1 text-caption flex items-center gap-1 ${isOverdueCarryover ? 'text-alert-text' : 'text-text-sub'}`}>
          <History size={12} aria-hidden />
          {isOverdueCarryover ? `${carryoverHint?.days_ago}日前の値です` : '前日から引き継ぎ'}
        </p>
      )}
      {field.source === 'ocr' && !field.is_not_applicable && (
        <p className="mt-1 text-caption text-text-sub">OCRで読み取った値です (変更するには理由の入力が必要です)</p>
      )}
      {errorMessage && <p role="alert" className="mt-1 text-caption text-danger-text">{errorMessage}</p>}
    </div>
  );
}
